import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useAtom } from "jotai";
import { useCreateSynonym, useUpdateSynonym } from "@/query/synonym";
import { editingSynonymAtom } from "@/atom/editing";
import { useAgentId } from "@/hook";
import Button from "@/component/Button";
import { errorToast, successToast } from "@/component/Toast";
import { TextInputGroup, MultiTextInputGroup, MultiTextInputGroupRef } from "@/component/input";


const SynonymEditForm : React.FC = () => {
    const agentId = useAgentId();
    const intl = useIntl();

    const synonymsInputRef = useRef<MultiTextInputGroupRef>(null);
    const [keyword, setKeyword] = useState<string>("");
    const [synonyms, setSynonyms] = useState<string[]>([]);
    const [validation, setValidation] = useState<{
        keyword: boolean,
        synonyms: boolean
    }>({
        keyword: true,
        synonyms: true
    });

    const [editingSynonym, setEditingSynonym] = useAtom(editingSynonymAtom);
    const { mutateAsync: create, isPending: isCreating } = useCreateSynonym(agentId);
    const { mutateAsync: update, isPending: isUpdating } = useUpdateSynonym(agentId);

    const clear = useCallback(() => {
        setKeyword("");
        setSynonyms([]);
        setValidation({
            keyword: true,
            synonyms: true
        });
        setEditingSynonym(undefined);
        synonymsInputRef.current?.clear();
    }, [setKeyword, setSynonyms, setEditingSynonym]);

    const validate = () => {
        const currentValidation = {
            keyword: !!keyword && keyword.trim().length > 0,
            synonyms: synonyms.length > 0
        };
        setValidation(currentValidation);
        return Object.values(currentValidation).every(value => value);
    };

    const onSubmit = async () => {
        if (!validate()) {
            return;
        }
        if(!editingSynonym) {
            await create({ keyword: keyword, synonyms: synonyms })
                .then(() => {
                    clear();
                    successToast(intl.formatMessage({ id: 't001' }));
                })
                .catch(() => {
                    errorToast(intl.formatMessage({ id: 't002' }));
                });
        } else {
            await update({ ...editingSynonym, keyword: keyword, synonyms: synonyms })
                .then(() => {
                    clear();
                    successToast(intl.formatMessage({ id: 't003' }));
                })
                .catch(() => {
                    errorToast(intl.formatMessage({ id: 't004' }));
                });
        }
    };

    useEffect(() => {
        return () => {
            clear();
        };
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if(!editingSynonym){
            clear();
            return;
        }
        setKeyword(editingSynonym.keyword);
        setSynonyms(editingSynonym.synonyms);
        setValidation({ keyword: true, synonyms: true });
    }, [editingSynonym, clear, setKeyword, setSynonyms]);

    return (
        <div role="form" className="form-container">
            <div className="form-container-input-group">
                <TextInputGroup rowGap={8}
                                value={keyword ?? ""}
                                label={intl.formatMessage({ id: 'i06010' })}
                                placeholder={intl.formatMessage({ id: 'i06011' })}
                                invalid={!validation.keyword && !keyword.trim()}
                                invalidText={intl.formatMessage({ id: "i06012" })}
                                onChange={setKeyword}
                />

                <MultiTextInputGroup ref={synonymsInputRef}
                                     value={synonyms}
                                     label={intl.formatMessage({ id: 'i06014' })}
                                     placeholder={intl.formatMessage({ id: 'i06013' })}
                                     invalid={!validation.synonyms && !synonyms.length}
                                     invalidText={intl.formatMessage({ id: "i06015" })}
                                     onChange={setSynonyms}
                />
            </div>

            <div className="flex gap-2">
                {editingSynonym && (
                    <Button size="full"
                            variant="secondary"
                            disabled={isUpdating || isCreating}
                            onClick={clear}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                )}
                <Button size="full"
                        variant="primary"
                        disabled={isUpdating || isCreating}
                        onClick={onSubmit}
                >
                    {intl.formatMessage({ id: editingSynonym ? "b0001" : "b0010" })}
                </Button>
            </div>
        </div>
    );
};

export default SynonymEditForm;